import {
  FETCH_FLEETADMIN_EARNING,
  FETCH_FLEETADMIN_EARNING_SUCCESS,
  FETCH_FLEETADMIN_EARNING_FAILED,
} from "../store/types";
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { get } from "firebase/database";

// Portal-only earnings rollup (settings.useReportRollups) — reads the
// CF-maintained /report_rollups/fleet_monthly node instead of scanning
// bookings. Mirrors the useCustomerOnDemand gate in usersactions.js: an
// absent/false flag, or any role outside the staff set (fleetadmin keeps its
// legacy fleet-scoped query; every mobile role too), keeps the legacy path
// below byte-identical. Fleet names stay client-resolved exactly like legacy.
const ROLLUP_ROLES = ['admin', 'finance', 'callcenteragent', 'callcentersupervisor'];
const useReportRollups = () => {
  try { return store.getState().settingsdata.settings.useReportRollups === true; } catch (_) { return false; }
};
const rollupEligible = (usertype) => ROLLUP_ROLES.indexOf(usertype) >= 0;
const MONTH_KEY_RE = /^\d{4}-\d{2}$/;
const ADDIS_OFFSET_MS = 3 * 60 * 60 * 1000;
const MONTHS_NAME = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
// Rollup month keys are Addis-time 'YYYY-MM'; `dated` is the first instant of
// that month (legacy used the first counted booking's tripdate — no view
// renders it, but the field is kept so the payload shape is unchanged).
const parseMonthKey = (mk) => {
  const year = parseInt(mk.slice(0, 4), 10);
  const month = parseInt(mk.slice(5, 7), 10) - 1;
  return { year: year, month: month, dated: Date.UTC(year, month, 1) - ADDIS_OFFSET_MS };
};
// Same batch name lookup the legacy branch does (one get(singleUserRef) per
// distinct fleetadmin) — duplicated rather than shared so the legacy block
// below stays untouched.
const resolveFleetadminNames = async (ids, singleUserRef) => {
  const fleetadminNames = {};
  await Promise.all(ids.map(async (fid) => {
    try {
      const userSnap = await get(singleUserRef(fid));
      if (userSnap.val()) {
        const user = userSnap.val();
        fleetadminNames[fid] = (user.firstName || '') + " " + (user.lastName || '');
      }
    } catch (e) { /* skip */ }
  }));
  return fleetadminNames;
};
// Map one fleet_monthly/{month}/{fleetadminUid} record to the exact legacy row.
const mapFleetMonth = (mk, fid, f, fleetadminNames, settings) => {
  const { year, month, dated } = parseMonthKey(mk);
  return {
    dated: dated,
    year: year,
    month: month,
    monthsName: MONTHS_NAME[month],
    fleetadminName: fleetadminNames[fid] || '',
    fleetCommission: num(f.fleetCommission).toFixed(settings.decimal),
    fleetUId: fid,
    uniqueKey: year + '_' + month + '_' + fid,
    total_rides: num(f.total_rides)
  };
};

export const fetchFleetAdminEarnings = () => async (dispatch) => {

  const {
    bookingListRef,
    settingsRef,
    singleUserRef,
    reportRollupFleetRef
  } = firebase;

  dispatch({
    type: FETCH_FLEETADMIN_EARNING,
    payload: null
  });

  const userInfo = store.getState().auth.profile;
  const settingsdata = await get(settingsRef);
  const settings = settingsdata.val();

  if (useReportRollups() && rollupEligible(userInfo && userInfo.usertype)) {
    try {
      const rollupSnap = await get(reportRollupFleetRef);
      const months = rollupSnap.val();
      const entries = [];
      const fleetadminIds = new Set();
      if (months) {
        Object.keys(months).filter(mk => MONTH_KEY_RE.test(mk) && months[mk]).forEach(mk => {
          Object.keys(months[mk]).forEach(fid => {
            const f = months[mk][fid];
            // Legacy only lists fleet months with a positive commission.
            if (f && num(f.total_rides) > 0 && num(f.fleetCommission) > 0) {
              entries.push({ mk: mk, fid: fid, f: f });
              fleetadminIds.add(fid);
            }
          });
        });
      }
      const fleetadminNames = await resolveFleetadminNames(Array.from(fleetadminIds), singleUserRef);
      const arr = entries.map(e => mapFleetMonth(e.mk, e.fid, e.f, fleetadminNames, settings));
      if (arr.length) {
        dispatch({
          type: FETCH_FLEETADMIN_EARNING_SUCCESS,
          payload: arr
        });
      } else {
        dispatch({
          type: FETCH_FLEETADMIN_EARNING_FAILED,
          payload: "No data available."
        });
      }
    } catch (error) {
      dispatch({
        type: FETCH_FLEETADMIN_EARNING_FAILED,
        payload: error.message
      });
    }
    return;
  }

  try {
    const snapshot = await get(bookingListRef(userInfo.uid, userInfo.usertype));
    if (snapshot.val()) {
      const mainArr = snapshot.val();
      var monthsName = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      var renderobj = {};

      // Collect unique fleetadmin IDs to fetch names in batch
      const fleetadminIds = new Set();
      Object.keys(mainArr).forEach(j => {
        if ((mainArr[j].status === 'PAID' || mainArr[j].status === 'COMPLETE') && mainArr[j].fleetCommission !== undefined && mainArr[j].fleetCommission > 0) {
          fleetadminIds.add(mainArr[j].fleetadmin);
        }
      });

      // Fetch all fleetadmin names at once (instead of nested onValue per entry)
      const fleetadminNames = {};
      await Promise.all(Array.from(fleetadminIds).map(async (fid) => {
        try {
          const userSnap = await get(singleUserRef(fid));
          if (userSnap.val()) {
            const user = userSnap.val();
            fleetadminNames[fid] = (user.firstName || '') + " " + (user.lastName || '');
          }
        } catch (e) { /* skip */ }
      }));

      Object.keys(mainArr).map(j => {
        if ((mainArr[j].status === 'PAID' || mainArr[j].status === 'COMPLETE') && mainArr[j].fleetCommission !== undefined && mainArr[j].fleetCommission > 0) {
          let bdt = new Date(mainArr[j].tripdate);
          let uniqueKey = bdt.getFullYear() + '_' + bdt.getMonth() + '_' + mainArr[j].fleetadmin;
          if (renderobj[uniqueKey] && renderobj[uniqueKey].fleetCommission > 0) {
            renderobj[uniqueKey].fleetCommission = (parseFloat(renderobj[uniqueKey].fleetCommission) + parseFloat(mainArr[j].fleetCommission)).toFixed(settings.decimal);
            renderobj[uniqueKey]['total_rides'] = renderobj[uniqueKey]['total_rides'] + 1;
          } else {
            renderobj[uniqueKey] = {};
            renderobj[uniqueKey]['dated'] = mainArr[j].tripdate;
            renderobj[uniqueKey]['year'] = bdt.getFullYear();
            renderobj[uniqueKey]['month'] = bdt.getMonth();
            renderobj[uniqueKey]['monthsName'] = monthsName[bdt.getMonth()];
            renderobj[uniqueKey]['fleetadminName'] = fleetadminNames[mainArr[j].fleetadmin] || '';
            renderobj[uniqueKey]['fleetCommission'] = parseFloat(mainArr[j].fleetCommission).toFixed(settings.decimal);
            renderobj[uniqueKey]['fleetUId'] = mainArr[j].fleetadmin;
            renderobj[uniqueKey]['uniqueKey'] = uniqueKey;
            renderobj[uniqueKey]['total_rides'] = 1;
          }
        }
        return null;
      });
      if (renderobj) {
        const arr = Object.keys(renderobj).map(i => {
          renderobj[i].fleetCommission = parseFloat(renderobj[i].fleetCommission).toFixed(settings.decimal)
          return renderobj[i]
        })
        dispatch({
          type: FETCH_FLEETADMIN_EARNING_SUCCESS,
          payload: arr
        });
      }

    } else {
      dispatch({
        type: FETCH_FLEETADMIN_EARNING_FAILED,
        payload: "No data available."
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_FLEETADMIN_EARNING_FAILED,
      payload: error.message
    });
  }
};
